/**
 * NeuGreen Energy — Native Slide Renderer
 * 12 code-rendered slides. No stock images. Colours from slidesData.PALETTE.
 */
import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Sun, Battery, Zap, Cpu, ShieldCheck, Gauge, Layers, TrendingUp, Building2, Award, Sparkles, Radio, Cable } from "lucide-react";
import { PALETTE as P } from "./slidesData";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.55, ease: "easeOut", delay },
});

const Eyebrow = ({ children, color = P.green }) => (
  <div className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color }}>{children}</div>
);
const H1 = ({ children }) => (
  <h1 className="font-bold tracking-tight text-[30px] sm:text-[38px] lg:text-[46px] leading-[1.08]" style={{ color: P.white }}>{children}</h1>
);
const Lede = ({ children }) => (
  <p className="text-[14px] sm:text-[15.5px] leading-[1.65] max-w-[62ch]" style={{ color: P.dim }}>{children}</p>
);

// ------------------------------------------------------- 1 · Cover
const CoverSlide = ({ c }) => (
  <div className="relative min-h-[560px] flex flex-col justify-between p-8 lg:p-14 overflow-hidden"
    style={{ background: `radial-gradient(1200px 700px at 88% 12%, ${P.blue}18 0%, transparent 65%), radial-gradient(900px 600px at 8% 90%, ${P.green}14 0%, transparent 60%), ${P.graphite}` }}>
    {/* Ecosystem hero photo — right side, low-opacity, blended */}
    <img
      src="/neugreen/hero-ecosystem.jpg"
      alt=""
      aria-hidden="true"
      className="absolute inset-y-0 right-0 w-[62%] h-full object-cover pointer-events-none"
      style={{ opacity: 0.42, maskImage: "linear-gradient(90deg, transparent 0%, black 55%, black 100%)", WebkitMaskImage: "linear-gradient(90deg, transparent 0%, black 55%, black 100%)" }}
    />
    <div className="absolute inset-0 pointer-events-none" style={{
      background: `linear-gradient(90deg, ${P.graphite} 0%, ${P.graphite}dd 30%, ${P.graphite}88 55%, transparent 100%)`,
    }} />

    {/* Decorative German-engineering rings */}
    <svg className="absolute top-8 right-8 opacity-30 pointer-events-none" width="360" height="360" viewBox="0 0 360 360">
      <defs>
        <linearGradient id="ngRing" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={P.green} />
          <stop offset="100%" stopColor={P.blue} />
        </linearGradient>
      </defs>
      <circle cx="240" cy="120" r="110" fill="none" stroke="url(#ngRing)" strokeWidth="1" />
      <circle cx="240" cy="120" r="70"  fill="none" stroke={P.silver} strokeOpacity="0.35" strokeWidth="1" strokeDasharray="3 5" />
      <circle cx="240" cy="120" r="34"  fill="none" stroke={P.green}  strokeOpacity="0.55" strokeWidth="1" />
    </svg>

    <motion.div {...fade(0)} className="relative">
      <Eyebrow color={P.silver}>{c.eyebrow}</Eyebrow>
    </motion.div>

    <motion.div {...fade(0.15)} className="relative max-w-2xl">
      <img
        src="/neugreen/logo-official.png"
        alt="NeuGreen Energy"
        className="mb-6 w-full max-w-[440px] h-auto"
        style={{ filter: `drop-shadow(0 20px 60px ${P.green}66) drop-shadow(0 0 40px ${P.blue}44)` }}
      />
      <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.3em]"
        style={{ border: `1px solid ${P.green}55`, background: `${P.green}12`, color: P.green }}>
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: P.green, boxShadow: `0 0 8px ${P.green}` }} /> {c.tag}
      </div>
      <p className="text-[17px] sm:text-[22px] leading-[1.45] max-w-[52ch]" style={{ color: P.ink }}>{c.subtitle}</p>
      <div className="mt-6 inline-flex items-center gap-3 px-4 py-2 rounded-md"
        style={{ background: `linear-gradient(90deg, ${P.green}22 0%, ${P.blue}22 100%)`, border: `1px solid ${P.silver}55`, backdropFilter: "blur(8px)" }}>
        <Sparkles size={14} style={{ color: P.green }} />
        <span className="text-[12.5px] font-semibold tracking-wide" style={{ color: P.white }}>{c.slogan}</span>
      </div>
    </motion.div>

    <motion.div {...fade(0.3)} className="relative flex items-center justify-between flex-wrap gap-4 pt-8 border-t" style={{ borderColor: P.border }}>
      <div className="flex items-center gap-3 text-[10.5px] uppercase tracking-[0.28em]" style={{ color: P.silver }}>
        <Sun size={12} /> Solar
        <span className="opacity-40">·</span>
        <Battery size={12} /> Speicher
        <span className="opacity-40">·</span>
        <Zap size={12} /> Mobilität
        <span className="opacity-40">·</span>
        <Cpu size={12} /> Digital
      </div>
      <div className="text-[10.5px] uppercase tracking-[0.28em]" style={{ color: P.silver }}>Made in Germany · Certified · Documented</div>
    </motion.div>
  </div>
);

// ------------------------------------------------------- 2 · Vision
const VisionSlide = ({ c }) => (
  <div className="p-8 lg:p-14 min-h-[560px]">
    <motion.div {...fade(0)}><Eyebrow>{c.eyebrow}</Eyebrow></motion.div>
    <motion.div {...fade(0.1)} className="mt-3"><H1>{c.title}</H1></motion.div>
    <motion.div {...fade(0.2)} className="mt-5"><Lede>{c.lede}</Lede></motion.div>
    <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
      {c.pillars.map((p, i) => (
        <motion.div key={i} {...fade(0.28 + i * 0.08)}
          className="relative p-6 rounded-lg overflow-hidden"
          style={{ background: P.surface, border: `1px solid ${P.border}` }}>
          <div className="absolute top-0 left-0 w-full h-[2px]" style={{ background: [P.green, P.blue, P.silver][i] }} />
          <div className="text-[10px] uppercase tracking-[0.28em] mb-3" style={{ color: [P.green, P.blue, P.silver][i] }}>
            0{i + 1}
          </div>
          <div className="text-[16px] font-semibold" style={{ color: P.white }}>{p.t}</div>
          <div className="mt-2 text-[12.5px] leading-snug" style={{ color: P.dim }}>{p.d}</div>
        </motion.div>
      ))}
    </div>
  </div>
);

// ------------------------------------------------------- 3 · Challenge
const ChallengeSlide = ({ c }) => (
  <div className="p-8 lg:p-14 min-h-[560px]">
    <motion.div {...fade(0)}><Eyebrow color="#F59E0B">{c.eyebrow}</Eyebrow></motion.div>
    <motion.div {...fade(0.1)} className="mt-3"><H1>{c.title}</H1></motion.div>
    <motion.div {...fade(0.2)} className="mt-5"><Lede>{c.lede}</Lede></motion.div>
    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-3">
      {c.points.map((p, i) => (
        <motion.div key={i} {...fade(0.28 + i * 0.06)}
          className="flex items-start gap-5 p-5 rounded-md"
          style={{ background: "rgba(245,158,11,0.05)", border: "1px solid rgba(245,158,11,0.28)" }}>
          <div className="shrink-0 font-bold text-[22px] leading-none min-w-[92px]" style={{ color: "#F59E0B" }}>{p.k}</div>
          <div className="text-[13px] leading-snug" style={{ color: P.ink }}>{p.v}</div>
        </motion.div>
      ))}
    </div>
  </div>
);

// ------------------------------------------------------- 4 · Ecosystem diagram
const EcosystemSlide = ({ c }) => (
  <div className="p-8 lg:p-14 min-h-[560px]">
    <motion.div {...fade(0)}><Eyebrow>{c.eyebrow}</Eyebrow></motion.div>
    <motion.div {...fade(0.1)} className="mt-3"><H1>{c.title}</H1></motion.div>
    <motion.div {...fade(0.2)} className="mt-5"><Lede>{c.lede}</Lede></motion.div>

    <div className="mt-10 relative">
      <svg viewBox="0 0 900 380" className="w-full">
        <defs>
          <linearGradient id="ngCenter" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%"  stopColor={P.green} />
            <stop offset="100%" stopColor={P.blue}  />
          </linearGradient>
        </defs>
        {/* Center node — NeuGreen */}
        <circle cx="450" cy="70" r="46" fill={P.graphite} stroke="url(#ngCenter)" strokeWidth="2" />
        <circle cx="450" cy="70" r="60" fill="none" stroke={P.green} strokeOpacity="0.2" />
        <text x="450" y="66" fontSize="12" fontWeight="700" fill={P.white} textAnchor="middle" fontFamily="Inter">NeuGreen</text>
        <text x="450" y="80" fontSize="10" fill={P.silver} textAnchor="middle" fontFamily="Inter">{c.center.s}</text>

        {/* Line down to brain */}
        <line x1="450" y1="116" x2="450" y2="180" stroke={P.silver} strokeOpacity="0.6" strokeDasharray="4 4" />

        {/* Brain node — AI EMS */}
        <rect x="345" y="180" width="210" height="60" rx="10" fill={P.graphite} stroke={P.green} strokeWidth="1.5" />
        <text x="450" y="204" fontSize="12" fontWeight="700" fill={P.green} textAnchor="middle" fontFamily="Inter">{c.brain.t}</text>
        <text x="450" y="222" fontSize="9.5" fill={P.silver} textAnchor="middle" fontFamily="Inter" letterSpacing="1.5">{c.brain.s.toUpperCase()}</text>

        {/* Branches */}
        {c.branches.map((b, i) => {
          const positions = [{ x: 160, color: P.green }, { x: 450, color: P.blue }, { x: 740, color: P.silver }];
          const pos = positions[i];
          return (
            <g key={i}>
              <line x1="450" y1="240" x2={pos.x} y2="300" stroke={pos.color} strokeOpacity="0.55" />
              <circle cx={pos.x} cy="320" r="42" fill={P.graphite} stroke={pos.color} strokeWidth="1.8" />
              <text x={pos.x} y="316" fontSize="12" fontWeight="700" fill={P.white} textAnchor="middle" fontFamily="Inter">{b.t}</text>
              <text x={pos.x} y="332" fontSize="9" fill={pos.color} textAnchor="middle" fontFamily="Inter" letterSpacing="1.5">{b.s.toUpperCase()}</text>
            </g>
          );
        })}
      </svg>
    </div>
  </div>
);

// ------------------------------------------------------- 5/6/7 · Layer (Solar / Storage / Mobility)
const LAYER_META = {
  solar:    { color: P.green,  icon: Sun,     label: "Solar Layer",    logo: "/neugreen/logo-bluesun.png"  },
  storage:  { color: P.blue,   icon: Battery, label: "Storage Layer",  logo: "/neugreen/logo-wattzing.png" },
  mobility: { color: P.silver, icon: Zap,     label: "Mobility Layer", logo: "/neugreen/logo-beny.png"     },
};

const LayerSlide = ({ c, layer, supplier, roleLabel }) => {
  const meta = LAYER_META[layer] || LAYER_META.solar;
  const Icon = meta.icon;
  return (
    <div className="grid lg:grid-cols-[1.1fr_0.9fr] min-h-[560px]">
      <div className="p-8 lg:p-14">
        <motion.div {...fade(0)}><Eyebrow color={meta.color}>{c.eyebrow}</Eyebrow></motion.div>
        <motion.div {...fade(0.1)} className="mt-3"><H1>{c.title}</H1></motion.div>
        <motion.div {...fade(0.2)} className="mt-5"><Lede>{c.lede}</Lede></motion.div>

        <div className="mt-6 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-[10.5px] uppercase tracking-[0.26em] font-semibold"
            style={{ background: `${meta.color}14`, border: `1px solid ${meta.color}55`, color: meta.color }}>
            {meta.logo && (
              <img src={meta.logo} alt={supplier}
                className="h-4 w-auto object-contain"
                style={{ filter: layer === "mobility" ? "none" : "brightness(1.15)" }} />
            )}
            <span>Supplier · {supplier}</span>
          </div>
          <div className="px-3 py-1.5 rounded-full text-[10.5px] uppercase tracking-[0.26em]"
            style={{ background: P.surface, border: `1px solid ${P.border}`, color: P.silver }}>
            {roleLabel}
          </div>
        </div>

        <div className="mt-8 space-y-3">
          {c.pillars.map((p, i) => (
            <motion.div key={i} {...fade(0.32 + i * 0.07)}
              className="flex items-start gap-4 p-4 rounded-md"
              style={{ background: P.surface, border: `1px solid ${meta.color}22` }}>
              <div className="shrink-0 w-8 h-8 rounded-md flex items-center justify-center text-[12px] font-bold"
                style={{ background: `${meta.color}20`, border: `1px solid ${meta.color}55`, color: meta.color }}>
                {i + 1}
              </div>
              <div>
                <div className="text-[14px] font-semibold" style={{ color: P.white }}>{p.t}</div>
                <div className="text-[12.5px] mt-1 leading-snug" style={{ color: P.dim }}>{p.d}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Right-side hero image with supplier logo badge */}
      <div className="relative border-l overflow-hidden min-h-[420px]"
        style={{ borderColor: P.border }}>
        <img
          src={`/neugreen/layer-${layer}.jpg`}
          alt={`${supplier} — ${meta.label}`}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0" style={{
          background: `linear-gradient(90deg, ${P.graphite} 0%, ${P.graphite}88 12%, transparent 32%, transparent 100%)`,
        }} />
        <div className="absolute inset-x-0 bottom-0 h-1/3" style={{
          background: `linear-gradient(180deg, transparent 0%, ${P.graphite}dd 100%)`,
        }} />
        {/* Supplier badge with official logo */}
        <motion.div {...fade(0.2)}
          className="absolute bottom-6 right-6 flex items-center gap-3 pl-3 pr-4 py-3 rounded-lg backdrop-blur-xl"
          style={{ background: "rgba(255,255,255,0.94)", border: `1px solid ${meta.color}66`, boxShadow: `0 12px 30px -10px ${meta.color}77` }}>
          <img src={meta.logo} alt={supplier} className="h-9 w-auto object-contain" />
          <div className="leading-none border-l pl-3" style={{ borderColor: `${P.graphite}22` }}>
            <div className="flex items-center gap-1.5">
              <Icon size={11} style={{ color: meta.color }} strokeWidth={2.2} />
              <div className="text-[8.5px] uppercase tracking-[0.24em] font-semibold" style={{ color: meta.color }}>{meta.label}</div>
            </div>
            <div className="text-[10.5px] font-mono mt-1" style={{ color: "#4B5563" }}>Selected technology source</div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// ------------------------------------------------------- 8 · Product Portfolio (BESS + Full Kits)
const PortfolioSlide = ({ c }) => (
  <div className="p-8 lg:p-14 min-h-[560px]">
    <motion.div {...fade(0)}><Eyebrow>{c.eyebrow}</Eyebrow></motion.div>
    <motion.div {...fade(0.1)} className="mt-3"><H1>{c.title}</H1></motion.div>
    <motion.div {...fade(0.2)} className="mt-5"><Lede>{c.lede}</Lede></motion.div>

    <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-5">
      {c.items.map((it, i) => {
        const accent = i === 0 ? P.green : P.blue;
        return (
          <motion.div key={i} {...fade(0.3 + i * 0.12)}
            className="relative rounded-lg overflow-hidden"
            style={{ background: P.surface, border: `1px solid ${accent}30` }}>
            {/* Image */}
            <div className="relative aspect-[16/10] overflow-hidden">
              <img src={it.image} alt={it.title} loading="lazy"
                className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 h-1/2" style={{
                background: `linear-gradient(180deg, transparent 0%, ${P.graphite}ee 100%)`,
              }} />
              <div className="absolute top-4 left-4 px-3 py-1 rounded-full text-[9.5px] uppercase tracking-[0.26em] font-semibold backdrop-blur-xl"
                style={{ background: "rgba(15,17,20,0.72)", border: `1px solid ${accent}66`, color: accent }}>
                {it.badge}
              </div>
              {it.supplierLogo && (
                <div className="absolute top-4 right-4 flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-md backdrop-blur-xl"
                  style={{ background: "rgba(255,255,255,0.94)", border: `1px solid ${accent}55`, boxShadow: `0 6px 18px -6px ${accent}77` }}>
                  <img src={it.supplierLogo} alt={it.supplierName} className="h-6 w-auto object-contain" />
                  <div className="text-[9px] uppercase tracking-[0.22em] font-semibold" style={{ color: "#374151" }}>
                    by {it.supplierName}
                  </div>
                </div>
              )}
            </div>
            {/* Content */}
            <div className="p-5">
              <div className="font-bold text-[19px] tracking-tight" style={{ color: P.white }}>{it.title}</div>
              <p className="mt-2 text-[13px] leading-snug" style={{ color: P.dim }}>{it.desc}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {it.specs.map((s, si) => (
                  <span key={si} className="text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-md font-mono"
                    style={{ background: `${accent}10`, border: `1px solid ${accent}33`, color: accent }}>
                    {s}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>

    <motion.div {...fade(0.7)} className="mt-6 flex items-center gap-3 px-4 py-3 rounded-md"
      style={{ background: `${P.silver}0a`, border: `1px solid ${P.silver}30` }}>
      <ShieldCheck size={14} style={{ color: P.silver }} />
      <div className="text-[12px]" style={{ color: P.ink }}>{c.note}</div>
    </motion.div>
  </div>
);

// ------------------------------------------------------- 9 · EMS
const EmsSlide = ({ c }) => {
  const icons = [Radio, Sun, Battery, Zap, Cpu, ShieldCheck];
  return (
    <div className="p-8 lg:p-14 min-h-[560px]">
      <motion.div {...fade(0)}><Eyebrow color={P.blue}>{c.eyebrow}</Eyebrow></motion.div>
      <motion.div {...fade(0.1)} className="mt-3"><H1>{c.title}</H1></motion.div>
      <motion.div {...fade(0.2)} className="mt-5"><Lede>{c.lede}</Lede></motion.div>

      {/* Mock dashboard band */}
      <motion.div {...fade(0.3)}
        className="mt-8 rounded-lg overflow-hidden"
        style={{ background: `linear-gradient(180deg, ${P.blue}14 0%, ${P.graphite} 100%)`, border: `1px solid ${P.blue}30` }}>
        <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: P.border }}>
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: P.green }} />
            <span className="text-[10.5px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.silver }}>NeuGreen EMS · Live</span>
          </div>
          <span className="text-[10px] uppercase tracking-[0.22em]" style={{ color: P.dim }}>solar 8.4 kW · storage 92% · grid −1.1 kW · ev 7.2 kW</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px" style={{ background: P.border }}>
          {c.panels.map((p, i) => {
            const Icon = icons[i] || Radio;
            return (
              <div key={i} className="p-5" style={{ background: P.graphite }}>
                <div className="flex items-center gap-2 mb-3">
                  <Icon size={14} style={{ color: P.blue }} />
                  <div className="text-[13px] font-semibold" style={{ color: P.white }}>{p.t}</div>
                </div>
                <div className="text-[12px] leading-snug" style={{ color: P.dim }}>{p.d}</div>
              </div>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
};

// ------------------------------------------------------- 9 · Technology Network
const NODE_LOGOS = {
  "Bluesun Solar":       "/neugreen/logo-bluesun.png",
  "Wattzing New Energy": "/neugreen/logo-wattzing.png",
  "BENY New Energy":     "/neugreen/logo-beny.png",
};

const NetworkSlide = ({ c }) => (
  <div className="p-8 lg:p-14 min-h-[560px]">
    <motion.div {...fade(0)}><Eyebrow color={P.silver}>{c.eyebrow}</Eyebrow></motion.div>
    <motion.div {...fade(0.1)} className="mt-3"><H1>{c.title}</H1></motion.div>
    <motion.div {...fade(0.2)} className="mt-5"><Lede>{c.lede}</Lede></motion.div>

    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {c.nodes.map((n, i) => {
        const logo = NODE_LOGOS[n.t];
        const accent = i % 3 === 0 ? P.green : i % 3 === 1 ? P.blue : P.silver;
        return (
          <motion.div key={i} {...fade(0.28 + i * 0.06)}
            className="relative p-5 rounded-lg flex flex-col"
            style={{ background: P.surface, border: `1px solid ${P.border}`, minHeight: 168 }}>
            <div className="absolute top-0 left-0 w-full h-[2px]" style={{ background: accent }} />
            {logo && (
              <div className="mb-3 h-8 flex items-center rounded px-2 py-1 self-start"
                style={{ background: "rgba(255,255,255,0.92)" }}>
                <img src={logo} alt={n.t} className="h-full w-auto object-contain" />
              </div>
            )}
            <div className="flex items-center gap-2 mb-1">
              <div className="w-1.5 h-1.5 rounded-full" style={{ background: P.green, boxShadow: `0 0 6px ${P.green}` }} />
              <div className="text-[14px] font-semibold" style={{ color: P.white }}>{n.t}</div>
            </div>
            <div className="text-[12.5px]" style={{ color: P.dim }}>{n.s}</div>
            <div className="mt-auto pt-3 text-[9.5px] uppercase tracking-[0.26em] font-mono" style={{ color: P.silver }}>{n.area}</div>
          </motion.div>
        );
      })}
    </div>
  </div>
);

// ------------------------------------------------------- 10 · Value (two columns)
const ValueSlide = ({ c }) => (
  <div className="p-8 lg:p-14 min-h-[560px]">
    <motion.div {...fade(0)}><Eyebrow>{c.eyebrow}</Eyebrow></motion.div>
    <motion.div {...fade(0.1)} className="mt-3"><H1>{c.title}</H1></motion.div>
    <motion.div {...fade(0.2)} className="mt-5"><Lede>{c.lede}</Lede></motion.div>

    <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-5">
      {c.columns.map((col, ci) => {
        const accent = ci === 0 ? P.blue : P.green;
        return (
          <motion.div key={ci} {...fade(0.28 + ci * 0.15)}
            className="p-6 rounded-lg"
            style={{ background: P.surface, border: `1px solid ${accent}30` }}>
            <div className="text-[10px] uppercase tracking-[0.28em] font-semibold mb-4" style={{ color: accent }}>
              {col.head}
            </div>
            <div className="space-y-3">
              {col.items.map((it, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="shrink-0 mt-1 w-1.5 h-1.5 rounded-full" style={{ background: accent, boxShadow: `0 0 6px ${accent}` }} />
                  <div>
                    <div className="text-[13.5px] font-semibold" style={{ color: P.white }}>{it.t}</div>
                    <div className="text-[12px] mt-0.5 leading-snug" style={{ color: P.dim }}>{it.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        );
      })}
    </div>
  </div>
);

// ------------------------------------------------------- 11 · Business Model
const BusinessSlide = ({ c }) => {
  const icons = [Layers, Building2, Award, TrendingUp, Cable];
  return (
    <div className="p-8 lg:p-14 min-h-[560px]">
      <motion.div {...fade(0)}><Eyebrow>{c.eyebrow}</Eyebrow></motion.div>
      <motion.div {...fade(0.1)} className="mt-3"><H1>{c.title}</H1></motion.div>
      <motion.div {...fade(0.2)} className="mt-5"><Lede>{c.lede}</Lede></motion.div>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
        {c.streams.map((s, i) => {
          const Icon = icons[i] || Layers;
          return (
            <motion.div key={i} {...fade(0.3 + i * 0.06)}
              className="p-5 rounded-md flex flex-col"
              style={{ background: P.surface, border: `1px solid ${P.border}` }}>
              <div className="w-9 h-9 rounded-md flex items-center justify-center mb-3"
                style={{ background: `${P.green}18`, border: `1px solid ${P.green}55` }}>
                <Icon size={15} style={{ color: P.green }} />
              </div>
              <div className="text-[13px] font-semibold leading-snug" style={{ color: P.white }}>{s.t}</div>
              <div className="text-[11.5px] mt-2 leading-snug" style={{ color: P.dim }}>{s.d}</div>
            </motion.div>
          );
        })}
      </div>

      <motion.div {...fade(0.7)} className="mt-6 flex items-center gap-3 px-4 py-3 rounded-md"
        style={{ background: `${P.blue}0d`, border: `1px solid ${P.blue}40` }}>
        <Gauge size={14} style={{ color: P.blue }} />
        <div className="text-[12px]" style={{ color: P.ink }}>{c.note}</div>
      </motion.div>
    </div>
  );
};

// ------------------------------------------------------- 12 · Contact
const ContactSlide = ({ c }) => (
  <div className="grid lg:grid-cols-[1.1fr_0.9fr] min-h-[560px]">
    <div className="p-8 lg:p-14 flex flex-col justify-between"
      style={{ background: `radial-gradient(700px 500px at 20% 80%, ${P.green}14 0%, transparent 65%)` }}>
      <div>
        <motion.div {...fade(0)}><Eyebrow>{c.eyebrow}</Eyebrow></motion.div>
        <motion.div {...fade(0.1)} className="mt-3"><H1>{c.title}</H1></motion.div>
        <motion.div {...fade(0.2)} className="mt-5"><Lede>{c.lede}</Lede></motion.div>
      </div>
      <motion.a {...fade(0.4)} href={`mailto:${c.contact.email}`}
        className="mt-10 inline-flex items-center gap-3 px-6 py-4 rounded-full font-bold text-[13px] uppercase tracking-[0.22em] transition hover:translate-y-[-2px] self-start"
        style={{ background: `linear-gradient(90deg, ${P.green} 0%, ${P.blue} 100%)`, color: P.graphite, boxShadow: `0 20px 60px -10px ${P.green}88` }}
        data-testid="neugreen-cta-button">
        {c.cta} <ArrowUpRight size={16} />
      </motion.a>
    </div>

    <div className="p-8 lg:p-14 border-l flex flex-col justify-center" style={{ borderColor: P.border, background: P.surface }}>
      <div className="text-[10px] uppercase tracking-[0.32em] font-semibold mb-6" style={{ color: P.silver }}>
        {c.contactTitle}
      </div>
      <div className="space-y-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: P.dim }}>Company</div>
          <div className="text-[22px] font-bold mt-1" style={{ color: P.white }}>{c.contact.brand}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: P.dim }}>Country</div>
          <div className="text-[15px] mt-1" style={{ color: P.ink }}>{c.contact.country}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: P.dim }}>Email</div>
          <a href={`mailto:${c.contact.email}`} className="text-[15px] mt-1 block hover:underline" style={{ color: P.green }}>{c.contact.email}</a>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: P.dim }}>Web</div>
          <div className="text-[15px] mt-1" style={{ color: P.ink }}>{c.contact.web}</div>
        </div>
      </div>
      <div className="mt-6 text-[12px] leading-snug" style={{ color: P.dim }}>{c.contact.note}</div>
    </div>
  </div>
);

// ------------------------------------------------------- Router
export function SlideRenderer({ slide, lang }) {
  const c = slide.i18n[lang] || slide.i18n.en;
  switch (slide.kind) {
    case "cover":     return <CoverSlide c={c} />;
    case "vision":    return <VisionSlide c={c} />;
    case "challenge": return <ChallengeSlide c={c} />;
    case "ecosystem": return <EcosystemSlide c={c} />;
    case "layer":     return <LayerSlide c={c} layer={slide.layer} supplier={slide.supplier} roleLabel={slide.role[lang] || slide.role.en} />;
    case "ems":       return <EmsSlide c={c} />;
    case "portfolio": return <PortfolioSlide c={c} />;
    case "network":   return <NetworkSlide c={c} />;
    case "value":     return <ValueSlide c={c} />;
    case "business":  return <BusinessSlide c={c} />;
    case "contact":   return <ContactSlide c={c} />;
    default:          return <div className="p-14 text-white">Missing layout: {slide.kind}</div>;
  }
}
